import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  Alert,
  Button,
  Empty,
  Input,
  Select,
  Spin,
  Tag,
  Typography,
} from "antd";
import { ArrowLeftOutlined, SearchOutlined } from "@ant-design/icons";
import { getInstitution } from "../../../api/institutionApi";
import { getAccountsByInstitutionId } from "../../../api/bankAccountApi";
import InstitutionAccountsTable from "../../../components/accounts/InstitutionAccountsTable";
import { accountColumns } from "../../../components/accounts/InstitutionAccountsColumns";
import { getTypeColor } from "../../../utils/themeUtils";

const { Title, Text } = Typography;

function InstitutionAccountsPage() {
  const { institutionId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [institution, setInstitution] = useState(null);
  const [bankAccounts, setBankAccounts] = useState([]);

  const [institutionLoading, setInstitutionLoading] = useState(true);
  const [accountsLoading, setAccountsLoading] = useState(true);

  const [institutionError, setInstitutionError] = useState("");
  const [accountsError, setAccountsError] = useState("");

  const [searchText, setSearchText] = useState(searchParams.get("search") || "");
  const [selectedBanks, setSelectedBanks] = useState(
    searchParams.get("banks") ? searchParams.get("banks").split(",") : []
  );
  const [selectedCurrencies, setSelectedCurrencies] = useState(
    searchParams.get("currencies") ? searchParams.get("currencies").split(",") : []
  );

  useEffect(() => {
    async function fetchInstitutionData() {
      try {
        setInstitutionLoading(true);
        setInstitutionError("");

        const data = await getInstitution(institutionId);
        setInstitution(data || null);
      } catch (err) {
        console.error("Failed to fetch institution:", err);
        setInstitutionError("Байгууллагын мэдээллийг ачаалж чадсангүй");
        setInstitution(null);
      } finally {
        setInstitutionLoading(false);
      }
    }

    if (institutionId) {
      fetchInstitutionData();
    }
  }, [institutionId]);

  useEffect(() => {
    async function fetchAccounts() {
      try {
        setAccountsLoading(true);
        setAccountsError("");

        const data = await getAccountsByInstitutionId(institutionId);
        setBankAccounts(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("Банкны дансны мэдээллийг ачаалж чадсангүй.", err);
        setAccountsError("Банкны дансны мэдээллийг ачаалж чадсангүй.");
        setBankAccounts([]);
      } finally {
        setAccountsLoading(false);
      }
    }

    if (institutionId) {
      fetchAccounts();
    }
  }, [institutionId]);

  useEffect(() => {
    const params = {};

    if (searchText) params.search = searchText;
    if (selectedBanks.length > 0) params.banks = selectedBanks.join(",");
    if (selectedCurrencies.length > 0) params.currencies = selectedCurrencies.join(",");

    setSearchParams(params);
  }, [searchText, selectedBanks, selectedCurrencies, setSearchParams]);

  function normalizeText(value) {
    return String(value || "")
      .toLowerCase()
      .replace(/\s+/g, "")
      .replace(/[^a-z0-9]/g, "");
  }

  const bankOptions = useMemo(() => {
    const values = [...new Set(bankAccounts.map((item) => item.bank).filter(Boolean))];

    return values.sort().map((value) => ({
      label: value,
      value,
    }));
  }, [bankAccounts]);

  const currencyOptions = useMemo(() => {
    const values = [...new Set(bankAccounts.map((item) => item.currency).filter(Boolean))];

    return values.sort().map((value) => ({
      label: value,
      value,
    }));
  }, [bankAccounts]);

  const filteredBankAccounts = useMemo(() => {
    const keyword = searchText.trim().toLowerCase();
    const normalizedKeyword = normalizeText(searchText);

    return bankAccounts.filter((item) => {
      const bankText = String(item.bank || "");
      const currencyText = String(item.currency || "");
      const accountNumberText = String(item.accountNumber || "");

      const matchesSearch =
        !keyword ||
        bankText.toLowerCase().includes(keyword) ||
        currencyText.toLowerCase().includes(keyword) ||
        accountNumberText.toLowerCase().includes(keyword) ||
        normalizeText(accountNumberText).includes(normalizedKeyword);

      const matchesBank =
        selectedBanks.length === 0 || selectedBanks.includes(item.bank);

      const matchesCurrency =
        selectedCurrencies.length === 0 || selectedCurrencies.includes(item.currency);

      return matchesSearch && matchesBank && matchesCurrency;
    });
  }, [bankAccounts, searchText, selectedBanks, selectedCurrencies]);

  const isFiltered =
    searchText.trim() !== "" ||
    selectedBanks.length > 0 ||
    selectedCurrencies.length > 0;

  function handleResetFilters() {
    setSearchText("");
    setSelectedBanks([]);
    setSelectedCurrencies([]);
  }

  function handleRemoveSearch() {
    setSearchText("");
  }

  function handleRemoveBank(value) {
    setSelectedBanks((prev) => prev.filter((item) => item !== value));
  }

  function handleRemoveCurrency(value) {
    setSelectedCurrencies((prev) => prev.filter((item) => item !== value));
  }

  if (institutionLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50">
        <Spin size="large" />
      </div>
    );
  }

  if (institutionError) {
    return (
      <div className="min-h-screen bg-slate-50 px-6 py-8">
        <Alert type="error" message={institutionError} showIcon className="rounded-2xl" />
      </div>
    );
  }

  if (!institution) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50">
        <Empty description="Байгууллага олдсонгүй" />
      </div>
    );
  }

  const institutionName = institution.name || "Трейдинг хаусууд";

  return (
    <div className="min-h-screen bg-slate-50 px-6 py-8">
      <div className="mx-auto">

        {/* One-row page header */}
        <div className="mb-6 flex items-center gap-3">
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate("/manage/institutions")}
            className="shrink-0"
          />
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <Title level={4} className="!mb-0 truncate text-slate-900">
                {institutionName}
              </Title>
              <Tag
                color={getTypeColor(institution?.type || "UNKNOWN")}
                className="shrink-0"
              >
                {institution?.type || "UNKNOWN"}
              </Tag>
            </div>
          </div>
        </div>

        {/* Compact inline filter row — no card, no labels */}
        <div className="mb-2 flex flex-wrap items-center gap-3">
          <Input
            placeholder="Банк, данс, валют хайх"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            allowClear
            prefix={<SearchOutlined className="text-slate-400" />}
            className="h-10 min-w-[200px] flex-1 rounded-xl sm:flex-none sm:w-[240px]"
          />
          <Select
            mode="multiple"
            allowClear
            className="min-w-[160px]"
            placeholder="Банк"
            value={selectedBanks}
            options={bankOptions}
            onChange={setSelectedBanks}
            maxTagCount="responsive"
          />
          <Select
            mode="multiple"
            allowClear
            className="min-w-[140px]"
            placeholder="Валют"
            value={selectedCurrencies}
            options={currencyOptions}
            onChange={setSelectedCurrencies}
            maxTagCount="responsive"
          />
          <Button
            onClick={handleResetFilters}
            disabled={!isFiltered}
            className="h-10 rounded-xl"
          >
            Цэвэрлэх
          </Button>
          <Text className="text-sm text-slate-400">
            {filteredBankAccounts.length}
            {isFiltered ? ` / ${bankAccounts.length}` : ""} данс
          </Text>
        </div>

        {/* Active filter tags — inline, no card */}
        {isFiltered ? (
          <div className="mb-5 flex flex-wrap items-center gap-2">
            {searchText ? (
              <Tag closable onClose={handleRemoveSearch} className="rounded-lg">
                Хайлт: {searchText}
              </Tag>
            ) : null}
            {selectedBanks.map((bank) => (
              <Tag
                key={bank}
                closable
                onClose={() => handleRemoveBank(bank)}
                className="rounded-lg"
              >
                Банк: {bank}
              </Tag>
            ))}
            {selectedCurrencies.map((currency) => (
              <Tag
                key={currency}
                closable
                onClose={() => handleRemoveCurrency(currency)}
                className="rounded-lg"
              >
                Валют: {currency}
              </Tag>
            ))}
            <button
              type="button"
              onClick={handleResetFilters}
              className="text-xs text-slate-400 underline underline-offset-2 hover:text-slate-600"
            >
              Бүгдийг цэвэрлэх
            </button>
          </div>
        ) : (
          <div className="mb-5" />
        )}

        {accountsError ? (
          <Alert
            type="error"
            message={accountsError}
            showIcon
            className="mb-5 rounded-2xl"
          />
        ) : null}

        <InstitutionAccountsTable
          title="Банкны данснууд"
          loading={accountsLoading}
          accounts={filteredBankAccounts}
          columns={accountColumns()}
          enableStatusChange={false}
        />
      </div>
    </div>
  );
}

export default InstitutionAccountsPage;